import React, { useEffect, useRef, useState } from "react";
import { Button } from "../components/ui/button";
import { ChevronRight, Phone, Menu, X, Shield, MessageCircle } from "lucide-react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { BRAND, LINKS } from "../mock";
import HeroScene from "./HeroScene";
import { BeamsBackground } from "@/components/ui/beams-background";

gsap.registerPlugin(ScrollTrigger);

export default function Hero() {
  const root = useRef(null);
  const overlayRef = useRef(null);
  const responseCardRef = useRef(null);
  const whatsappHighlightRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);

  // Removed fade-in animations - elements should be immediately visible

  // GSAP Glow Effect for Response Card
  useEffect(() => {
    const card = responseCardRef.current;
    if (!card) return;

    const ctx = gsap.context(() => {
      // Create a continuous glow animation
      gsap.to(card, {
        boxShadow: "0 0 30px rgba(34, 197, 94, 0.4), 0 0 60px rgba(34, 197, 94, 0.2)",
        duration: 2,
        ease: "power2.inOut",
        yoyo: true,
        repeat: -1
      });
    }, root);
    
    return () => ctx.revert();
  }, []);

  // GSAP WhatsApp Highlight Animation
  useEffect(() => {
    const highlight = whatsappHighlightRef.current;
    if (!highlight) return;

    const ctx = gsap.context(() => {
      // Set initial state
      gsap.set(highlight, {
        backgroundColor: "transparent",
        color: "inherit"
      });

      // Create timeline for highlight animation
      const tl = gsap.timeline({ delay: 1.2 });
      
      tl.to(highlight, {
        backgroundColor: "#E4161A", // Brand red
        color: "#ffffff",
        scale: 1.05,
        duration: 0.6,
        ease: "power2.out"
      })
      .to(highlight, {
        scale: 1,
        duration: 0.3,
        ease: "power2.inOut"
      })
      .to(highlight, {
        boxShadow: "0 0 20px rgba(228, 22, 26, 0.4)",
        duration: 0.4,
        ease: "power2.inOut"
      });

      // Add a subtle pulse effect that repeats
      gsap.to(highlight, {
        boxShadow: "0 0 25px rgba(228, 22, 26, 0.6), 0 0 35px rgba(228, 22, 26, 0.3)",
        duration: 2,
        ease: "power2.inOut",
        yoyo: true,
        repeat: -1,
        delay: 2
      });
    }, root);
    
    return () => ctx.revert();
  }, []);

  useEffect(() => {
    const overlay = overlayRef.current;
    if (!overlay) return;
    
    const tl = gsap.timeline();
    
    if (menuOpen) {
      // Reset initial state
      gsap.set(overlay, { display: "block", opacity: 0, y: "-100%" });
      
      // Animate opening
      tl.to(overlay, {
        opacity: 1,
        y: "0%",
        duration: 0.4,
        ease: "power3.out"
      })
      .fromTo(".mobile-nav-link", 
        { opacity: 0, y: 30 },
        { 
          opacity: 1, 
          y: 0, 
          duration: 0.3, 
          stagger: 0.1, 
          ease: "power2.out" 
        },
        "-=0.2"
      );
    } else {
      // Animate closing
      tl.to(overlay, {
        opacity: 0,
        y: "-100%",
        duration: 0.3,
        ease: "power3.in",
        onComplete: () => gsap.set(overlay, { display: "none" })
      });
    }
  }, [menuOpen]);

  // Close on ESC
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const handleMenuToggle = () => {
    setMenuOpen(!menuOpen);
  };

  const closeMenu = () => setMenuOpen(false);

  return (
    <section ref={root} id="start" className="relative overflow-hidden bg-white text-neutral-900">
      {/* 3D canvas fills right half */}
      <div className="absolute inset-0 z-0">
        <BeamsBackground className="absolute inset-0 bg-transparent" intensity="strong" />
        <div className="noise-overlay" />
        <div className="hero-vignette" />
      </div>

      {/* Top Nav */}
      <div className="mx-auto max-w-7xl px-6 pt-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src={BRAND.assets.logoDark} alt="HAK Logo" className="h-12 md:h-16 w-auto select-none" />
        </div>
      </div>

      {/* Sticky Burger Menu */}
      <button
        className="fixed top-6 right-6 z-50 inline-flex items-center justify-center rounded-full p-3 bg-white/90 backdrop-blur-sm border border-black/10 text-neutral-700 hover:bg-white hover:text-neutral-900 transition-all duration-200 shadow-lg hover:shadow-xl"
        aria-label="Menü öffnen"
        onClick={handleMenuToggle}
      >
        <Menu className="h-6 w-6" />
      </button>

      {/* Hero Content */}
      <div className="mx-auto max-w-7xl px-6 pb-24 pt-8 md:pt-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-stretch">
          {/* Left: content */}
          <div className="md:col-span-7 relative">
            {/* BVkSH Logo Badge moved above response tag */}
            <div className="inline-flex items-center gap-3 px-4 py-2 bg-white/80 backdrop-blur-sm rounded-lg border border-black/10 shadow-sm mb-3">
              <div className="flex items-center justify-center w-8 h-8 bg-[var(--brand-red)] rounded-lg">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <div className="text-xs text-neutral-600">
                <div className="font-semibold">Mitglied im BVkSH</div>
                <div className="mb-1">Geprüfte Qualität</div>
                <div className="flex items-center gap-0.5" aria-label="5 von 5 Sternen">
                  <svg className="w-3 h-3 text-red-500 fill-current" viewBox="0 0 20 20"><path d="M10 15l-5.5 3 1.5-6.5L0 7l6.5-.5L10 0l3.5 6.5L20 7l-6 4.5L15.5 18z"/></svg>
                  <svg className="w-3 h-3 text-red-500 fill-current" viewBox="0 0 20 20"><path d="M10 15l-5.5 3 1.5-6.5L0 7l6.5-.5L10 0l3.5 6.5L20 7l-6 4.5L15.5 18z"/></svg>
                  <svg className="w-3 h-3 text-red-500 fill-current" viewBox="0 0 20 20"><path d="M10 15l-5.5 3 1.5-6.5L0 7l6.5-.5L10 0l3.5 6.5L20 7l-6 4.5L15.5 18z"/></svg>
                  <svg className="w-3 h-3 text-red-500 fill-current" viewBox="0 0 20 20"><path d="M10 15l-5.5 3 1.5-6.5L0 7l6.5-.5L10 0l3.5 6.5L20 7l-6 4.5L15.5 18z"/></svg>
                  <svg className="w-3 h-3 text-red-500 fill-current" viewBox="0 0 20 20"><path d="M10 15l-5.5 3 1.5-6.5L0 7l6.5-.5L10 0l3.5 6.5L20 7l-6 4.5L15.5 18z"/></svg>
                </div>
              </div>
            </div>

            <div className="relative z-10">
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-extrabold leading-[1.1] tracking-tight">
              KFZ Gutachter Köln | 60 Min WhatsApp‑Service
            </h1>
              <p className="mt-5 text-lg md:text-xl text-neutral-700 max-w-2xl">
                Unfallgutachten, Schadensgutachten &amp; KFZ-Bewertungen in Köln und Umgebung. Robotik-Vermessung, alle Fahrzeugtypen, BVSKH-zertifiziert.
            </p>
              <div className="mt-4">
                <span 
                  ref={whatsappHighlightRef}
                  className="whatsapp-highlight inline-block px-2 py-1 rounded-md text-sm font-medium transition-all duration-300"
                >
                  WhatsApp uns jetzt!
                </span>
              </div>
              <div 
                ref={responseCardRef}
                className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-black/5 px-4 py-1.5 text-sm text-neutral-700 mt-6 mb-4 relative"
              >
                {/* Pulsing green dot */}
                <div className="relative">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <div className="absolute inset-0 w-2 h-2 bg-green-500 rounded-full animate-ping"></div>
                </div>
                Antwort innerhalb von 60 Minuten
              </div>
              <div className="mt-4 flex flex-col sm:flex-row gap-4">
                <Button asChild className="h-14 px-7 text-base bg-[var(--brand-red)] hover:bg-red-600 text-white">
                <a href={LINKS.whatsappLink} target="_blank" rel="noreferrer">
                  WhatsApp starten
                    <ChevronRight className="ml-2 h-5 w-5" />
                </a>
              </Button>
                <Button asChild variant="secondary" className="h-14 px-7 text-base bg-black/5 hover:bg-black/10 text-neutral-900">
                <a href={LINKS.telLink}>
                    <Phone className="mr-2 h-5 w-5" />
                  Anrufen
                </a>
              </Button>
            </div>
              <p className="mt-4 text-neutral-500" style={{ fontFamily: 'Caveat, cursive', fontSize: '1.1rem' }}>
                Unfallbericht &amp; Kontaktdaten einfach per WhatsApp senden – oder kurz anrufen.
              </p>
              
            </div>
          </div>

          {/* Right: car image aligned to text block height */}
          <div className="md:col-span-5 h-full flex items-end justify-end">
            <img
              src={process.env.PUBLIC_URL + "/auto.png"}
              alt="Sportwagen"
              className="select-none h-full w-auto object-contain"
              loading="eager"
            />
          </div>
        </div>
          </div>

      {/* Fullscreen overlay menu */}
      <div ref={overlayRef} className="fixed inset-0 z-50 bg-white/95 backdrop-blur-sm" style={{ display: "none" }} role="dialog" aria-modal="true" onClick={closeMenu}>
        <div className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between px-6 py-5 border-b border-black/10" onClick={(e) => e.stopPropagation()}>
          <img src={BRAND.assets.logoDark} alt="HAK Logo" className="h-9 w-auto" />
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-md p-2 text-neutral-700 hover:bg-black/5 transition-colors relative z-30"
            aria-label="Menü schließen"
            onClick={closeMenu}
          >
            <X className="h-6 w-6" />
          </button>
        </div>
        <div className="flex h-full items-center justify-center relative" onClick={(e) => e.stopPropagation()}>
          {/* Decorative car image */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <img 
              src={process.env.PUBLIC_URL + "/auto3.png"} 
              alt=""
              className="w-[85%] max-w-[800px] opacity-[0.03] select-none object-contain"
              aria-hidden="true"
            />
          </div>

          {/* Navigation links */}
          <div className="flex flex-col items-center gap-8 text-3xl font-semibold text-neutral-900 relative z-10">
            <a href="#ablauf" onClick={closeMenu} className="mobile-nav-link hover:text-neutral-600 transition-colors">Ablauf</a>
            <a href="#warum" onClick={closeMenu} className="mobile-nav-link hover:text-neutral-600 transition-colors">Warum wir</a>
            <a href="#kontakt" onClick={closeMenu} className="mobile-nav-link hover:text-neutral-600 transition-colors">Kontakt</a>
            
            {/* WhatsApp Button */}
            <div className="mobile-nav-link mt-4">
              <a 
                href={LINKS.whatsappLink} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 px-8 py-4 bg-green-500 hover:bg-green-600 text-white text-xl font-semibold rounded-full transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl"
                onClick={closeMenu}
              >
                <MessageCircle className="h-6 w-6" />
                WhatsApp starten
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}